/**
 * Géométrie du recadrage carré, puis rendu de l'image envoyée au serveur.
 *
 * Les décalages sont exprimés en fraction du côté du cadre, jamais en pixels :
 * le cadre rétrécit sur mobile et change avec l'orientation de l'appareil, et
 * une géométrie en pixels obligerait à mesurer le DOM pour rester juste.
 */

/**
 * Côté de l'image envoyée. Le serveur réduit ensuite à 256 avec libvips, dont
 * le rééchantillonnage vaut mieux que celui du canvas : 512 ne coûte que
 * quelques dizaines de kilo-octets et laisse cette réduction au meilleur outil.
 */
export const AVATAR_UPLOAD_SIZE = 512;

/** Au-delà, le zoom n'agrandit plus que les pixels de la photo. */
export const AVATAR_MAX_ZOOM = 4;

const AVATAR_UPLOAD_QUALITY = 0.9;

/** Un navigateur qui ignore le WebP retombe sur le PNG, que le serveur accepte aussi. */
const AVATAR_UPLOAD_TYPE = "image/webp";

export interface CropGeometry {
  /** Dimensions naturelles, orientation EXIF déjà appliquée par le navigateur. */
  imageWidth: number;
  imageHeight: number;
  /** 1 = l'image couvre exactement le cadre. */
  zoom: number;
  /** Position du bord haut-gauche de l'image, en fraction du côté du cadre. */
  offsetX: number;
  offsetY: number;
}

export interface CropOffsets {
  offsetX: number;
  offsetY: number;
}

/** Dimensions affichées de l'image, en fraction du côté du cadre. */
export interface CropSize {
  width: number;
  height: number;
}

/** Rectangle source en pixels de l'image, tel que `drawImage` l'attend. */
export interface CropRect {
  x: number;
  y: number;
  size: number;
}

const shortestSide = (imageWidth: number, imageHeight: number) =>
  Math.max(1, Math.min(imageWidth, imageHeight));

export function displayedSize(geometry: CropGeometry): CropSize {
  const cover = geometry.zoom / shortestSide(geometry.imageWidth, geometry.imageHeight);
  return { width: geometry.imageWidth * cover, height: geometry.imageHeight * cover };
}

/** Empêche tout bord vide : l'image couvre le cadre quelle que soit la manipulation. */
export function clampOffsets(geometry: CropGeometry): CropOffsets {
  const { width, height } = displayedSize(geometry);
  return {
    offsetX: Math.min(0, Math.max(1 - width, geometry.offsetX)),
    offsetY: Math.min(0, Math.max(1 - height, geometry.offsetY)),
  };
}

/** Cadrage d'ouverture : le sujet centré, ce qui déborde réparti des deux côtés. */
export function centeredGeometry(imageWidth: number, imageHeight: number): CropGeometry {
  const geometry: CropGeometry = { imageWidth, imageHeight, zoom: 1, offsetX: 0, offsetY: 0 };
  const { width, height } = displayedSize(geometry);
  return { ...geometry, offsetX: (1 - width) / 2, offsetY: (1 - height) / 2 };
}

/**
 * Zoom ancré sur un point du cadre, exprimé lui aussi en fraction du côté : le
 * pixel sous les doigts y reste.
 */
export function zoomAround(
  geometry: CropGeometry,
  zoom: number,
  focusX: number,
  focusY: number,
): CropGeometry {
  const next = Math.min(AVATAR_MAX_ZOOM, Math.max(1, zoom));
  const ratio = next / geometry.zoom;
  const moved: CropGeometry = {
    ...geometry,
    zoom: next,
    offsetX: focusX - (focusX - geometry.offsetX) * ratio,
    offsetY: focusY - (focusY - geometry.offsetY) * ratio,
  };
  return { ...moved, ...clampOffsets(moved) };
}

/** Déplacement du cadrage, deltas eux aussi en fraction du côté du cadre. */
export function panBy(geometry: CropGeometry, deltaX: number, deltaY: number): CropGeometry {
  const moved: CropGeometry = {
    ...geometry,
    offsetX: geometry.offsetX + deltaX,
    offsetY: geometry.offsetY + deltaY,
  };
  return { ...moved, ...clampOffsets(moved) };
}

/**
 * Rectangle réellement découpé. Le cadre affiché et ce calcul partent de la
 * même géométrie : l'aperçu ne peut pas mentir sur ce qui part au serveur.
 */
export function cropRectOf(geometry: CropGeometry): CropRect {
  const side = shortestSide(geometry.imageWidth, geometry.imageHeight);
  const { offsetX, offsetY } = clampOffsets(geometry);
  // `Math.max` absorbe le cheveu de flottant qui ferait sortir le rectangle de
  // l'image après un pincement, ce que `drawImage` traduirait par un bord vide.
  return {
    x: Math.max(0, (-offsetX * side) / geometry.zoom),
    y: Math.max(0, (-offsetY * side) / geometry.zoom),
    size: side / geometry.zoom,
  };
}

/**
 * Découpe et réencode : ce sont ces octets-là qui partent, jamais le fichier
 * d'origine. Les métadonnées de la photo — EXIF, GPS — restent sur l'appareil.
 */
export async function cropToBlob(source: CanvasImageSource, rect: CropRect): Promise<Blob> {
  const size = AVATAR_UPLOAD_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("Ton navigateur n’a pas pu préparer l’image.");
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(source, rect.x, rect.y, rect.size, rect.size, 0, 0, size, size);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, AVATAR_UPLOAD_TYPE, AVATAR_UPLOAD_QUALITY);
  });
  if (blob === null) {
    throw new Error("Ton navigateur n’a pas pu préparer l’image.");
  }
  return blob;
}
